import { Injectable } from '@angular/core';
import {HttpRequest, HttpResponse} from "@angular/common/http";
import {mergeMap, Observable, of, ReplaySubject} from "rxjs";
import {take} from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class CacheResolveService {
  constructor() { }
  private cache = new Map<string, { subject: ReplaySubject<HttpResponse<any>>, expiration: Date }>();
  allowedPostRoutes: string[] = ['api/auth/v1/checktoken'];
  getOrSetResponse(req: HttpRequest<any>, ttl: number, fetcher: () => Observable<any>): Observable<HttpResponse<any>> {
    let key = req.method + ":" + req.urlWithParams;

    // Ajoutez des informations supplémentaires dans la clé pour les requêtes POST.
    if (req.method === 'POST') {
      const body = JSON.stringify(req.body); // Utilisez un vrai hash dans la pratique.
      key += `:body:${body}`;
    }

    const now = new Date();
    const cacheEntry = this.cache.get(key);

    // Traitement spécifique pour les GET: toujours utiliser le cache si disponible.
    if (req.method === 'GET' && cacheEntry && cacheEntry.expiration > now) {
      // console.log(`[Cache Hit] Utilisation du cache pour GET ${key}`);
      return cacheEntry.subject.asObservable();
    }

    // Pour les POST: utiliser le cache uniquement si le statut est 200.
    if (req.method === 'POST' && cacheEntry && cacheEntry.expiration > now) {
      return cacheEntry.subject.pipe(
        take(1),
        mergeMap(cachedResponse => {
          if (cachedResponse instanceof HttpResponse && cachedResponse.status === 200) {
            // console.log(`[Cache Hit] Utilisation du cache pour POST ${key}`);
            return of(cachedResponse);
          } else {
            // console.log(`[Cache Ignored] Réponse en cache ignorée pour POST ${key} due au statut ${cachedResponse.status}`);
            return fetcher(); // Exécuter la requête si la réponse en cache n'est pas 200.
          }
        })
      );
    }

    // console.log(`[Cache Miss] Requête au serveur pour ${key}`);
    const subject = new ReplaySubject<HttpResponse<any>>(1);

    fetcher().subscribe({
      next: response => {
        // Mise à jour du cache pour GET sans condition, pour POST uniquement si statut 200.
        if (req.method === 'GET' || (req.method === 'POST' && response instanceof HttpResponse && response.status === 200)) {
          // console.log(`[Mise en cache] Mise à jour du cache avec la réponse pour ${key}`);
          this.cache.set(key, { subject, expiration: new Date(now.getTime() + ttl * 1000) });
        }
        subject.next(response);
      },
      error: error => {
        // console.log(`[Error] Erreur lors de la récupération de l'URL: ${key}`, error);
        subject.error(error);
      },
      complete: () => {
        // console.log(this.cache)
        subject.complete()
      }
    });

    return subject.asObservable();
  }

  clearCache(url?: string) {
    if (url) {
      this.cache.delete(url);
    } else {
      this.cache.clear();
    }
  }
}
